import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import useCopagoCashbook from '../../hooks/useCopagoCashbook';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const CopagoCashbook = () => {
  const { getHistory, uploadFile, downloadFile, getBatchZipURL, isLoading } = useCopagoCashbook();

  const [history, setHistory] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const canUseTools = true;

  const fetchHistory = useCallback(async () => {
    if (!canUseTools) return;
    try {
      const data = await getHistory();
      setHistory(data || []);
    } catch (err) {
      toast.error('Fehler beim Laden der Historie');
    }
  }, [canUseTools]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFileChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      const list = Array.from(e.target.files);
      setFiles(list);
    }
  };

  const readFileAsText = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = event => resolve(event.target.result);
      reader.onerror = () => reject(new Error('Fehler beim Lesen der Datei'));
      reader.readAsText(file);
    });

  const handleUpload = async () => {
    if (!files || files.length === 0) return;

    try {
      for (const file of files) {
        const csv = await readFileAsText(file);
        const response = await uploadFile(csv, file.name);

        if (response && response.record && response.record._id) {
          await handleDownload(response.record._id, 'generated', response.record.newFileName);
        }
      }

      toast.success('Dateien erfolgreich konvertiert');
      setFiles([]);
      const fileInput = document.getElementById('cashbook-file-input');
      if (fileInput) fileInput.value = '';
      fetchHistory();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error(err.message || 'Fehler beim Hochladen der Dateien');
    }
  };

  const handleDownload = async (id, type, fileName) => {
    try {
      const blob = await downloadFile(id, type);
      if (!blob) {
        throw new Error('Kein Download-Inhalt verfügbar');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Download fehlgeschlagen');
    }
  };

  const isSelected = id => selectedIds.includes(id);

  const toggleSelect = (id, checked) => {
    setSelectedIds(prev => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const toggleSelectAllOnPage = checked => {
    if (!history || history.length === 0) return;
    if (checked) {
      setSelectedIds(Array.from(new Set([...selectedIds, ...history.map(r => r._id)])));
    } else {
      setSelectedIds(prev => prev.filter(id => !history.find(r => r._id === id)));
    }
  };

  const handleBatchDownload = async type => {
    if (!selectedIds || selectedIds.length < 2) {
      toast.error('Bitte mindestens zwei Einträge auswählen');
      return;
    }

    try {
      const response = await getBatchZipURL(selectedIds, type);
      if (response && response.preSignedURL) {
        window.open(response.preSignedURL, '_blank');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Fehler beim Erstellen des ZIP-Downloads');
    }
  };

  if (!canUseTools) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 2 }}>
        <Typography>Keine Berechtigung.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='h6' gutterBottom>
        Copago Kassenbuch Konvertierung
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12}>
            <Typography variant='subtitle1' gutterBottom>
              Neue CSV-Datei hochladen
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Erwartetes Format: UTF-8 CSV mit Semikolon als Trennzeichen und ohne Kopfzeile. Der
              Dateiname muss den Text&nbsp;
              <strong>„Filiale &lt;Nr&gt;“</strong> enthalten.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box display='flex' gap={2} alignItems='center'>
              <Button variant='outlined' component='label' sx={{ flexGrow: 1 }}>
                Datei auswählen
                <input
                  id='cashbook-file-input'
                  type='file'
                  accept='.csv'
                  multiple
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleUpload}
                disabled={!files.length || isLoading}
              >
                {isLoading ? 'Verarbeite…' : 'Konvertieren'}
              </Button>
            </Box>
            {files.length > 0 && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                Ausgewählt: {files.map(f => f.name).join(', ')}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='subtitle1'>Historie</Typography>
          <Box display='flex' gap={1}>
            <Button
              size='small'
              variant='outlined'
              onClick={() => handleBatchDownload('original')}
              disabled={selectedIds.length < 2}
            >
              ZIP Original
            </Button>
            <Button
              size='small'
              variant='contained'
              onClick={() => handleBatchDownload('generated')}
              disabled={selectedIds.length < 2}
            >
              ZIP Generiert
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <input
                    type='checkbox'
                    checked={history.length > 0 && selectedIds.length === history.length}
                    ref={null}
                    onChange={e => toggleSelectAllOnPage(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Datum</TableCell>
                <TableCell>Benutzer</TableCell>
                <TableCell>Original Dateiname</TableCell>
                <TableCell>Neuer Dateiname</TableCell>
                <TableCell>Filiale</TableCell>
                <TableCell align='right'>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map(record => (
                <TableRow key={record._id} hover>
                  <TableCell padding='checkbox'>
                    <input
                      type='checkbox'
                      checked={isSelected(record._id)}
                      onChange={e => toggleSelect(record._id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{dayjs(record.meta.createdAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                  <TableCell>{record.meta.createdBy}</TableCell>
                  <TableCell title={record.originalFileName}>{record.originalFileName}</TableCell>
                  <TableCell title={record.newFileName}>{record.newFileName}</TableCell>
                  <TableCell>
                    {record.branch
                      ? `${record.branch.locationName} (${record.subName || '-'})`
                      : '-'}
                  </TableCell>
                  <TableCell align='right'>
                    <Button
                      size='small'
                      variant='outlined'
                      sx={{ mr: 1 }}
                      onClick={() =>
                        handleDownload(record._id, 'original', record.originalFileName)
                      }
                    >
                      Original
                    </Button>
                    <Button
                      size='small'
                      variant='contained'
                      onClick={() => handleDownload(record._id, 'generated', record.newFileName)}
                    >
                      Generiert
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Einträge gefunden.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CopagoCashbook;
